import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import NotificationPanel from "@/components/NotificationPanel/NotificationPanel";
import {
  getNotificationLogs,
  getNotificationLogsCount,
  getNotificationLogsMarkAsRead,
  type NotificationLog,
} from "@/services/api/userProfile";

interface NotificationContextValue {
  notificationCount: number;
  notifications: NotificationLog[];
  isNotificationOpen: boolean;
  isNotificationLoading: boolean;
  openNotifications: () => void;
  closeNotifications: () => void;
  refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(
  undefined,
);

const resolveNotificationLogs = (payload: unknown): NotificationLog[] => {
  if (Array.isArray(payload)) {
    return payload as NotificationLog[];
  }

  if (payload && typeof payload === "object") {
    const payloadWithResponses = payload as {
      response?: NotificationLog[];
      data?: NotificationLog[];
    };

    if (Array.isArray(payloadWithResponses.response)) {
      return payloadWithResponses.response;
    }

    if (Array.isArray(payloadWithResponses.data)) {
      return payloadWithResponses.data;
    }
  }

  return [];
};

const resolveNotificationCount = (payload: unknown) => {
  if (typeof payload === "number") {
    return payload;
  }

  if (payload && typeof payload === "object") {
    const payloadCount = (payload as { count?: unknown }).count;

    if (typeof payloadCount === "number") {
      return payloadCount;
    }

    const nestedData = (payload as { data?: { count?: unknown } | number }).data;

    if (typeof nestedData === "number") {
      return nestedData;
    }

    if (
      nestedData &&
      typeof nestedData === "object" &&
      typeof nestedData.count === "number"
    ) {
      return nestedData.count;
    }
  }

  return 0;
};

export const NotificationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [notifications, setNotifications] = useState<NotificationLog[]>([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isNotificationLoading, setIsNotificationLoading] = useState(false);

  const refreshNotifications = useCallback(async () => {
    const [notificationLogsResult, notificationCountResult] =
      await Promise.allSettled([
        getNotificationLogs(),
        getNotificationLogsCount(),
      ]);

    if (notificationLogsResult.status === "fulfilled") {
      setNotifications(resolveNotificationLogs(notificationLogsResult.value.data));
    }

    if (notificationCountResult.status === "fulfilled") {
      setNotificationCount(
        resolveNotificationCount(notificationCountResult.value.data),
      );
    }
  }, []);

  useEffect(() => {
    void refreshNotifications();
  }, [refreshNotifications]);

  const openNotifications = useCallback(async () => {
    setIsNotificationOpen(true);
    setIsNotificationLoading(true);

    const [
      notificationLogsResult,
      notificationCountResult,
      markAsReadResult,
    ] = await Promise.allSettled([
      getNotificationLogs(),
      getNotificationLogsCount(),
      getNotificationLogsMarkAsRead(),
    ]);

    let nextNotifications = notifications;

    if (notificationLogsResult.status === "fulfilled") {
      nextNotifications = resolveNotificationLogs(notificationLogsResult.value.data);
    }

    if (markAsReadResult.status === "fulfilled") {
      nextNotifications = nextNotifications.map((notification) => ({
        ...notification,
        isRead: true,
        read: true,
      }));
      setNotificationCount(0);
    } else if (notificationCountResult.status === "fulfilled") {
      setNotificationCount(
        resolveNotificationCount(notificationCountResult.value.data),
      );
    }

    setNotifications(nextNotifications);
    setIsNotificationLoading(false);
  }, [notifications]);

  const contextValue = useMemo(
    () => ({
      notificationCount,
      notifications,
      isNotificationOpen,
      isNotificationLoading,
      openNotifications: () => {
        void openNotifications();
      },
      closeNotifications: () => setIsNotificationOpen(false),
      refreshNotifications,
    }),
    [
      isNotificationLoading,
      isNotificationOpen,
      notificationCount,
      notifications,
      openNotifications,
      refreshNotifications,
    ],
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <NotificationPanel
        isLoading={isNotificationLoading}
        notifications={notifications}
        onClose={() => setIsNotificationOpen(false)}
        open={isNotificationOpen}
        unreadCount={notificationCount}
      />
    </NotificationContext.Provider>
  );
};

export const useNotificationCenter = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error(
      "useNotificationCenter must be used within a NotificationProvider",
    );
  }

  return context;
};
